/* eslint-disable prettier/prettier */
import React, { useEffect, useState } from 'react';
import { useGlobal } from '@rcg/ui-react';
import {
  Box,
  Card,
  Accordion,
  AccordionSummary,
  FormControlLabel,
  FormControl,
  FormHelperText,
  Grid,
  Input,
  Radio,
  RadioGroup,
  Typography,
  InputAdornment,
} from '@material-ui/core';
import { Formik } from 'formik';
import getSymbolFromCurrency from 'currency-symbol-map';
import MaskedInput from 'react-text-mask';
import createNumberMask from 'text-mask-addons/dist/createNumberMask';

import { useQuery } from '@apollo/client';
import { formatPrice } from '@rcg/pricing-format';
import useStyles from '../MakePayment/index.style';
import useStylesPaymentOption from './index.style';
import paymentValidationSchema from '../MakePayment/index.validation';
import PaymentHistory from '../PaymentHistory';
import { GET_PAYMENT_HISTORY } from '../../graphql/queries';
import { getSessionStorageItem } from '../../commons/utils';
import { BOOKING_TYPES, SESSION_STORAGE_CURRENT_BOOKING } from '../../commons/constants';

const PaymentOption = ({
  rooms,
  setRadioButtonSelected,
  setAmountToPay,
  price: { balanceDue, balanceDueDate, currencyCode, inFinal, total, totalBalanceDue },
  setNotification,
  setShowLoader,
  setCurrentBalanceToPay,
  setShowPaymentMethod,
  setErroramountToPay,
  errorAmountToPay,
  setDisableGiftCards,
}) => {
  const {
    renderContent,
    data: { localizedDayjs },
  } = useGlobal();
  const classes = useStyles();
  const classPayment = useStylesPaymentOption();
  const [showPanel, setShowPanel] = useState(false);
  const [showInput, setShowInput] = useState(false);
  const [customValue, setCustomValue] = useState(0);

  const price = { balanceDue, balanceDueDate, currencyCode, inFinal };

  const radioHandleChange = (handleChange) => (event) => {
    setRadioButtonSelected(event.target.value);
    if (event.target.value === 'remainingBalance' || rooms.length > 1) {
      setShowPanel(false);
      setShowInput(false);
      setShowPaymentMethod(true);
      setCurrentBalanceToPay(price.balanceDue);
      setAmountToPay(price.balanceDue);
      setErroramountToPay(false);
      setDisableGiftCards(false);
    } else {
      setShowInput(true);
      if (!errorAmountToPay) {
        setCurrentBalanceToPay(customValue);
        setAmountToPay(customValue);
      }
    }
    handleChange(event.target.name)(event.target.value);
  };
  const isGrpBooking =
    getSessionStorageItem(SESSION_STORAGE_CURRENT_BOOKING)?.type === BOOKING_TYPES.GRP;
  const isTABooking =
    getSessionStorageItem(SESSION_STORAGE_CURRENT_BOOKING)?.type === BOOKING_TYPES.TA;
  
  const {
    called: paymentHistoryCalled,
    error: dataError,
    loading: loadPayment,
    data: paymentData,
  } = useQuery(GET_PAYMENT_HISTORY, { fetchPolicy: 'no-cache', skip: isGrpBooking || isTABooking });

  let paymentHistoryData;
  if (paymentHistoryCalled && !loadPayment && !dataError) {
    paymentHistoryData = paymentData.currentBooking.paymentHistory;
  }

  useEffect(() => {
    if (dataError) {
      setNotification({
        title: renderContent('paymentBookingAmendPaymentHistoryErrorTitle', 'Technical Issues'),
        description: renderContent(
          'paymentBookingAmendPaymentHistoryMessageError',
          'We are unable to retrieve the payment information. Please try again later',
        ),
        type: 'error',
        open: true,
      });
    }
    setShowLoader(loadPayment);
  }, [dataError, loadPayment]);

  const handleInput = (handleChange) => (e) => {
    const stringAmount = e.target.value.replace(/,/g, '');
    const amount = +stringAmount;
    const payment = balanceDue - amount;
    if (amount >= 1 && payment >= 0) {
      setAmountToPay(+stringAmount);
      setCurrentBalanceToPay(+stringAmount);
      setCustomValue(+stringAmount);
      setShowPaymentMethod(true);
      setErroramountToPay(false);
      setDisableGiftCards(false);
    } else {
      setErroramountToPay(true);
      setDisableGiftCards(true);
    }
    handleChange(e.target.name)(e.target.value);
  };

  const numberMaskPayment = createNumberMask({
    prefix: '',
    suffix: '',
    includeThousandsSeparator: true,
    thousandsSeparatorSymbol: ',',
    allowDecimal: true,
    decimalSymbol: '.',
    decimalLimit: 2,
    integerLimit: 7,
    allowNegative: false,
    allowLeadingZeroes: false,
  });

  const paymentBalanceFar = total - totalBalanceDue;

  const inputAdornment = (
    <InputAdornment position="start">{getSymbolFromCurrency(currencyCode)}</InputAdornment>
  );

  return (
    <Card className={classes.rootCard}>
      <Formik
        initialValues={{
          paymentValue: '',
        }}
        validationSchema={paymentValidationSchema(renderContent, balanceDue)}
        >
        {(props) => (
          <form data-testid="form" autoComplete="off">
            {balanceDue > 0 && (
              <Accordion className={classes.pannel} expanded={showPanel}>
                <AccordionSummary
                  aria-controls="panel-content"
                  id="panel-header"
                  className={classPayment.summaryContentPayment}>
                  <Box width="100%">
                    <FormControl fullWidth>
                      <RadioGroup
                        data-testid="payment-radio-group"
                        aria-label="paymentValue"
                        name="paymentValue"
                        value={props.values.paymentValue || ''}
                        onChange={radioHandleChange(props.handleChange)}>
                        <Grid
                          container
                          direction="row"
                          justifyContent="space-between"
                          alignItems="center"
                          className={classes.details}>
                          <Grid item sm={8} xs={10} md={6}>
                            <Typography className={classes.detailsTitle}>
                              {balanceDue > 0
                                ? renderContent(
                                    `paymentBookingPanelPayRemainingBalance`,
                                    `Pay Remaining Balance`,
                                  )
                                : renderContent('paymentBookingAmendPaidInFull', 'Paid in Full')}
                            </Typography>
                            {balanceDue > 0 && (
                              <Typography className={classes.detailsSubtitle}>
                                {renderContent('paymentBookingAmendDueLabel', 'Due')}{' '}
                                {localizedDayjs(balanceDueDate).format('MMM D, YYYY')}
                              </Typography>
                            )}
                          </Grid>
                          {balanceDue > 0 && (
                            <>
                              <Grid item sm={8} xs={10} md={6}>
                                <Typography
                                  data-testid="balance-due"
                                  className={classPayment.amountPayment}>
                                  {formatPrice(balanceDue, currencyCode, true, true, true)}
                                </Typography>
                              </Grid>
                              <FormControlLabel
                                value="remainingBalance"
                                control={(
                                  <Radio
                                    onChange={props.handleChange}
                                    inputProps={{ 'data-testid': 'remaining-balance-radio' }}
                                    className={classes.radioButton}
                                  />
                                )}
                              />
                            </>
                          )}
                        </Grid>
                        {balanceDue > 0 && !inFinal && (
                          <Grid
                            container
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                            className={classes.details}>
                            <Grid item sm={6} xs={12}>
                              <Typography className="detailsTitle">
                                {renderContent('paymentBookingPanelCustomAmt', 'Pay Custom Amount')}
                              </Typography>
                            </Grid>
                            <Grid
                              item
                              sm={12}
                              xs={12}
                              md={6}
                              className={classPayment.customInputPayment}>
                              {showInput && (
                                <div>
                                  <MaskedInput
                                    defaultValue=""
                                    autoFocus
                                    mask={numberMaskPayment}
                                    name="amount"
                                    onBlur={props.handleBlur}
                                    onChange={handleInput(props.handleChange)}
                                    error={props.errors.amount && props.touched.amount}
                                    value={props.values.amount}
                                    inputProps={{ 'data-testid': 'custom-amount' }}
                                    startAdornment={inputAdornment}
                                    render={(ref, renderProps) => {
                                      return <Input inputRef={ref} {...renderProps} />;
                                    }}
                                  />
                                  <FormHelperText
                                    error={
                                      props.errors && props.touched.amount && props.errors.amount
                                    }>
                                    {props.errors.amount}
                                  </FormHelperText>
                                </div>
                              )}
                            </Grid>
                            <FormControlLabel
                              value="customAmount"
                              control={(
                                <Radio
                                  onChange={props.handleChange}
                                  inputProps={{ 'data-testid': 'custom-amount-radio-input' }}
                                  className={classes.radioButton}
                                />
                              )}
                            />
                          </Grid>
                        )}
                      </RadioGroup>
                    </FormControl>
                  </Box>
                </AccordionSummary>
              </Accordion>
            )}
            <PaymentHistory
              price={price}
              paymentHistoryData={paymentHistoryData}
              classNewLayout
              paymentBalanceFar={formatPrice(paymentBalanceFar, currencyCode, true, true, true)}
              from="payment-page"
            />
          </form>
        )}
      </Formik>
    </Card>
  );
};

export default PaymentOption;
